import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiMail, FiSearch, FiSend } from 'react-icons/fi';
import "../../../assets/css/doctor/doctorpages/DoctorMessages.css";
import DoctorMasterLayout from "../doctorlayouts/DoctorMasterLayout";

// Adjust to match wherever the rest of the doctor panel keeps its API base
// URL / auth token (e.g. an axios instance, .env value, or auth context).
const BASE_URL = 'https://backend.bizdevit.com/api';
const getToken = () => localStorage.getItem('doctor_token');

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${getToken()}` },
});

const DoctorMessages = () => {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [replyText, setReplyText] = useState('');
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const fetchMessages = useCallback(async (search = '') => {
    setLoadingList(true);
    setError(null);
    try {
      const res = await axios.get(`${BASE_URL}/doctor/Messages/list`, {
        ...authHeaders(),
        params: search ? { search } : {},
      });
      setMessages(res.data.data || []);
    } catch (err) {
      setError('Failed to load messages.');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Debounce search so we're not hitting the API on every keystroke
  useEffect(() => {
    const handle = setTimeout(() => {
      fetchMessages(searchQuery);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchQuery, fetchMessages]);

  const handleMessageClick = async (message) => {
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${BASE_URL}/doctor/Messages/${message.id}`, authHeaders());
      setSelectedMessage(res.data.data);

      // Reflect read status locally without waiting for a full refetch
      if (message.unread) {
        setMessages((prev) =>
          prev.map((m) => (m.id === message.id ? { ...m, unread: false } : m))
        );
      }
    } catch (err) {
      setError('Failed to load message.');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedMessage) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/doctor/Messages/${selectedMessage.id}/reply`,
        { body: replyText },
        authHeaders()
      );
      setSelectedMessage((prev) => ({
        ...prev,
        replies: [
          ...(prev.replies || []),
          {
            id: res.data.data.id,
            sender_type: 'doctor',
            body: replyText,
            time: 'Just now',
          },
        ],
      }));
      setReplyText('');
    } catch (err) {
      setError('Failed to send reply.');
    } finally {
      setSending(false);
    }
  };

  const handleArchive = async (id) => {
    try {
      await axios.patch(`${BASE_URL}/doctor/Messages/${id}/archive`, {}, authHeaders());
      setMessages((prev) => prev.filter((m) => m.id !== id));
      if (selectedMessage?.id === id) setSelectedMessage(null);
    } catch (err) {
      setError('Failed to archive message.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/doctor/Messages/${id}`, authHeaders());
      setMessages((prev) => prev.filter((m) => m.id !== id));
      if (selectedMessage?.id === id) setSelectedMessage(null);
    } catch (err) {
      setError('Failed to delete message.');
    }
  };

  const unreadCount = messages.filter((m) => m.unread).length;

  return (
    <DoctorMasterLayout>
      <div className="dm-body">
        <div className="dm-container">
          {/* Header */}
          <div className="dm-header">
            <div className="dm-header-left">
              <h2 className="dm-title">Messages</h2>
              <p className="dm-subtitle">Conversations and replies from your patients</p>
            </div>
            <span className="dm-unread-badge">{unreadCount} Unread</span>
          </div>

          {/* Split layout */}
          <div className="dm-layout">
            {/* ── List panel ── */}
            <div className="dm-list-panel">
              <div className="dm-search-wrap">
                <div className="dm-search-box">
                  <FiSearch />
                  <input
                    type="text"
                    placeholder="Search messages..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {error && <p className="dm-error">{error}</p>}

              <div className="dm-list">
                {loadingList ? (
                  <p className="dm-loading">Loading...</p>
                ) : messages.length === 0 ? (
                  <p className="dm-list-empty">No messages found.</p>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      className={`dm-item${message.unread ? ' dm-item-unread' : ''}${
                        selectedMessage?.id === message.id ? ' dm-item-active' : ''
                      }`}
                      onClick={() => handleMessageClick(message)}
                    >
                      <div className="dm-avatar">{message.patientName.charAt(0)}</div>
                      <div className="dm-item-content">
                        <div className="dm-item-header">
                          <h4 className="dm-item-name">{message.patientName}</h4>
                          <span className="dm-item-time">{message.time}</span>
                        </div>
                        <p className="dm-item-subject">{message.subject}</p>
                        <p className="dm-item-preview">{message.preview}</p>
                      </div>
                      {message.unread && <div className="dm-unread-dot"></div>}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* ── Detail panel ── */}
            <div className="dm-detail-panel">
              {loadingDetail ? (
                <div className="dm-empty">
                  <p>Loading message...</p>
                </div>
              ) : selectedMessage ? (
                <>
                  <div className="dm-detail-header">
                    <div className="dm-patient-info">
                      <div className="dm-avatar-large">
                        {selectedMessage.patientName.charAt(0)}
                      </div>
                      <div>
                        <h3 className="dm-patient-name">{selectedMessage.patientName}</h3>
                        <p className="dm-detail-subject">{selectedMessage.subject}</p>
                      </div>
                    </div>
                    <div className="dm-actions">
                      <button className="dm-btn-archive" onClick={() => handleArchive(selectedMessage.id)}>
                        Archive
                      </button>
                      <button className="dm-btn-delete" onClick={() => handleDelete(selectedMessage.id)}>
                        Delete
                      </button>
                    </div>
                  </div>

                  <div className="dm-thread">
                    <div className="dm-bubble dm-bubble-patient">
                      <div className="dm-bubble-time">{selectedMessage.time}</div>
                      <p className="dm-bubble-text">{selectedMessage.fullMessage}</p>
                    </div>

                    {selectedMessage.replies?.map((reply) => (
                      <div key={reply.id} className={`dm-bubble dm-bubble-${reply.sender_type}`}>
                        <div className="dm-bubble-time">{reply.time}</div>
                        <p className="dm-bubble-text">{reply.body}</p>
                      </div>
                    ))}
                  </div>

                  <div className="dm-reply-section">
                    <textarea
                      placeholder="Type your reply..."
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      className="dm-reply-textarea"
                    />
                    <div className="dm-reply-actions">
                      <button className="dm-btn-send" onClick={handleSendReply} disabled={sending}>
                        <FiSend /> {sending ? 'Sending...' : 'Send Reply'}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="dm-empty">
                  <FiMail />
                  <p>Select a message to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DoctorMasterLayout>
  );
};

export default DoctorMessages;